import { CallbackRegistry, default as CallbackFactory, UnsubscribeFunction } from "callback-registry";
import { Glue42Office } from "../../glue4office";
import {
    InternalSheetAPI, ShowGridParams, ValidateShowGridInvocationArgs, ValidationResponse,
} from "./types";

import { convertParams, flatten, camelize } from "./utils";

async function executeAsync(registry: CallbackRegistry, key: any, ...args: any[]): Promise<any> {
    const results: any[] = registry.execute(key, ...args);
    // tslint:disable-next-line:no-console
    await Promise.all(results.map(async (result) => {
        if (result && typeof result.then === "function") {
            await result;
        }
        return result;
    }));
}

export default class SheetImpl implements InternalSheetAPI {
    public get asSheetAPI(): Glue42Office.Excel.Sheet {
        const that = this;
        if (!this._sheet) {
            this._sheet = {
                name: this._name,
                workbook: this._workbook,
                options: this._options,
                get data() {
                    return that._data;
                },
                get columnConfig() {
                    return that._columns;
                },
                update: (data: object[]): Promise<void> => this.update(data),
                onChanged: (callback: (data: object[], errorCallback: (errors: Glue42Office.Excel.ValidationError[]) => void, doneCallback: () => void, delta: Glue42Office.Excel.DeltaItem[]) => Promise<void>): UnsubscribeFunction => this.onChanged(callback),
                onChanging: (callback: (info: Glue42Office.Excel.SheetChangingInfo) => void): UnsubscribeFunction => this.onChanging(callback),
                changeColumnConfig: (columnConfig: Glue42Office.Excel.ColumnConfig[], data?: object[]): Promise<void> => this.changeColumnConfig(columnConfig, data),
            };
        }
        return this._sheet;
    }

    private _sheet: Glue42Office.Excel.Sheet;
    private _registry: CallbackRegistry = CallbackFactory({ errorHandling: "throw" });

    private _id: string;
    private _name: string;
    private _data: any;
    private _workbook: string;
    private _columns: Glue42Office.Excel.ColumnConfig[];
    private _options: Glue42Office.Excel.OpenSheetOptions;
    private _validationResponse: ValidationResponse;
    private _invokeMethod: (params: any, options: any) => Promise<object>;
    private _deltaCache: Glue42Office.Excel.DeltaItem[][];

    constructor(id: string, config: Glue42Office.Excel.OpenSheetConfig, invokeShowGrid: (params: any, options: any) => Promise<object>) {
        this._id = id;
        this._name = config.options ? config.options.worksheet : "";
        this._data = config.data;
        this._workbook = config.options ? config.options.workbook : "";
        this._columns = config.columnConfig;
        this._options = config.options;
        this._invokeMethod = invokeShowGrid;
    }

    public changeColumnConfig(columnConfig: Glue42Office.Excel.ColumnConfig[], data?: object[]): Promise<void> {
        return new Promise<void>((resolve, reject): void => {
            if (!columnConfig) {
                reject("Can not change column in the sheet without columnConfig");
                return;
            }
            if (!data) {
                data = this._data;
            }
            const convertedParams = convertParams(columnConfig, data, this._options, this._id);
            const successHandler = () => {
                this._data = data;
                resolve();
            };
            this._invokeMethod(convertedParams.params, convertedParams.agmOptions)
                .then(successHandler)
                .catch(reject);
        });
    }

    public update(data: object[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (!data || !Array.isArray(data)) {
                reject("Can not update sheet with empty or incorrect type `data`");
                return;
            }
            const convertedParams = convertParams(this._columns, data, this._options, this._id);

            this._invokeMethod(convertedParams.params, convertedParams.agmOptions)
                .then(() => {
                    // this._data = [...data];
                    resolve();
                })
                .catch(reject);
        });
    }

    public onChanged(
        callback: (
            data: object,
            errorCallback: (errors: Glue42Office.Excel.ValidationError[]) => void,
            doneCallback: () => void,
            delta: Glue42Office.Excel.DeltaItem[]) => Promise<void>): UnsubscribeFunction {
        return this._registry.add("on-changed", (data, error, done, delta) => callback(data, error, done, delta));
    }

    public onChanging(
        callback: (info: Glue42Office.Excel.SheetChangingInfo) => void): UnsubscribeFunction {
        return this._registry.add("on-changing", callback);
    }

    public async _onUpdate(args: ValidateShowGridInvocationArgs): Promise<ValidationResponse> {
        this._name = args.worksheet;
        this._workbook = args.workbook;
        let delta: Glue42Office.Excel.DeltaItem[];
        let columns: any;

        this._registry.execute(
            "on-changing",
            {
                chunkIndex: args.chunkIndex,
                totalChunks: args.totalChunks,
            });

        try {
            delta = JSON.parse(args.dataAsJSON);
            columns = JSON.parse(args.columnsAsJSON);
        } catch (error) {
            this._deltaCache = null;
            // tslint:disable-next-line:no-console
            console.error(error);
            return;
        }

        // Merge with current data
        this._data = this.mergeData(args, delta, columns);

        if (!args.totalChunks ||
            args.updateType !== "row") {
            this._deltaCache = null;
        } else {
            if (args.chunkIndex === 0) {
                this._deltaCache = [delta];
            } else if (this._deltaCache) {
                this._deltaCache.push(delta);
            }
        }

        if (!args.totalChunks ||
            args.chunkIndex === args.totalChunks - 1) {
            try {
                const allDeltas = this._deltaCache && flatten(this._deltaCache);
                await executeAsync(this._registry,
                    "on-changed",
                    this._data,
                    this.errorCallback,
                    this.doneCallback,
                    allDeltas);

            } finally {
                this._deltaCache = null;
            }

        } else {
            this._validationResponse = {
                isValid: true,
            };
        }
        return this._validationResponse;
    }

    private errorCallback = (errors: Glue42Office.Excel.ValidationError[]) => {
        this._validationResponse = {
            isValid: false,
            errorsAsJSON: JSON.stringify(errors),
        };
    }

    private doneCallback = () => {
        this._validationResponse = {
            isValid: true,
        };
    }

    private mergeData(
        args: ValidateShowGridInvocationArgs,
        delta: Glue42Office.Excel.DeltaItem[],
        columnConfig: Glue42Office.Excel.ColumnConfig[]): object[] {
        if (args.updateType === "image") {
            let columnHeaderRow = delta.find((d) => d.rowAfterIndex === 1)?.row;
            // tslint:disable-next-line:no-console

            if (!columnHeaderRow) {
                columnHeaderRow = [...columnConfig];
                if (!columnHeaderRow || columnHeaderRow.length === 0) {
                    // tslint:disable-next-line:no-console
                    console.error("No columns received update will be ignored !");
                }
            }

            const data = delta.reduce((res, currDelta, index) => {
                if (index !== 0) {
                    const row: any = {};
                    currDelta.row.forEach((rowValue, col) => {
                        const colHeader = columnHeaderRow[col];
                        if (colHeader !== undefined && colHeader !== null) {
                            const headerSameAsConfig = columnConfig.find((c) => c.header === colHeader);
                            let colName;
                            if (headerSameAsConfig) {
                                colName = headerSameAsConfig.fieldName || camelize(colHeader);
                            } else {
                                colName = camelize(colHeader);
                            }
                            row[colName] = rowValue;
                        }
                    });

                    res.push(row);
                }
                return res;
            }, [] as object[]);

            return data;
        }

        let dataAfterMerged: object[] = [];
        const isRowMode: boolean = (args.updateType === "row");
        if (isRowMode) {
            // We need to add the old data, because our mode is row, so we
            // will receive only the rows, which are modified, deleted and inserted
            dataAfterMerged = [...this._data];
        }

        // G4E-1089 - in case of deletion, apply changes back-to-front to avoid having to recalculate
        // indices after every deletion
        // NB:  we're expecting changed chunks to come in reverse order for the same reason (otherwise the Excel plugin
        // would have to do the recalculation itself)
        const from = (delta[0].action === "deleted" ? delta.length - 1 : 0);
        const toExclusive = (delta[0].action === "deleted" ? -1 : delta.length);
        const step = (delta[0].action === "deleted" ? -1 : +1);
        for (let ii = from; ii !== toExclusive; ii += step) {
            const deltaItem = delta[ii];

            const { rowBeforeIndex, rowAfterIndex, action, row, rowAfter: deltaRowAfter } = deltaItem;

            const beforeActualIndex = rowBeforeIndex - 1;
            const afterActualIndex = rowAfterIndex - 1;
            const rowAfter = deltaRowAfter || row;

            // delta between rowAfter and rowBefore in case of 'row'
            // in case of 'image', the actual row
            if (!action || !isRowMode) {
                if (rowAfter.length) {
                    const obj = {};
                    this._columns.forEach((x, i) => {
                        obj[x.fieldName] = rowAfter[i];
                    });
                    dataAfterMerged[afterActualIndex] = obj;
                } else {
                    dataAfterMerged[afterActualIndex] = rowAfter;
                }
            } else if (action === "unchanged") {
                // This action is used only in image mode, so we need to copy the unchanged data to the updated index
                dataAfterMerged[afterActualIndex] = this._data[beforeActualIndex];
            } else if (action === "deleted") {
                // We need to delete a certain row, only if we are in row mode
                // if we are in image mode, we don't care for deleted rows
                if (isRowMode) {
                    dataAfterMerged.splice(beforeActualIndex >= 0 ? beforeActualIndex : 0, deltaItem.count || 1);
                }
            } else if (action === "modified") {
                Object.keys(row).forEach((i) => {
                    const updateFieldData = row[i];
                    if (updateFieldData === null &&
                        (!rowAfter || rowAfter[i] || rowAfter[i] === "" || rowAfter[i] === 0)) {
                        // this means that the data in this field is unchanged
                        return;
                    }
                    const fieldName: string = this._columns[i].fieldName;
                    const rowData: any = this._data[beforeActualIndex];
                    rowData[fieldName] = updateFieldData;
                    dataAfterMerged[beforeActualIndex] = rowData;
                });
            } else if (action === "inserted") {
                const insertedObj: any = {};
                Object.keys(rowAfter).forEach((updateFieldDataIx, i) => {
                    const fieldName: string = this._columns[i].fieldName;
                    insertedObj[fieldName] = rowAfter[updateFieldDataIx] || undefined;
                });
                dataAfterMerged.splice(beforeActualIndex, 0, insertedObj);
            }
        }

        return dataAfterMerged;
    }
}
